import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useWebsiteSite from "./hooks/useWebsiteSite";
import WebsiteCreateWizard from "./WebsiteCreateWizard";
import WebsiteOverview from "./WebsiteOverview";
import openStorefrontBuilder from "./utils/openStorefrontBuilder";
import { isRealEstateSite } from "./utils/vertical";

function WebsiteSettingsPage() {
  const state = useWebsiteSite();
  const { loading, loadFailed, error, setError, notice, setNotice, sites, site, templates } = state;
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const showWizard = !loading && !loadFailed && (creating || !sites.length);

  const onCreate = async (payload, customDomain) => {
    const created = await state.createSite(payload, customDomain);
    if (created) {
      setCreating(false);
      setJustCreated(true);
    }
  };

  const onOpenBuilder = () => {
    const message = openStorefrontBuilder(site?.id);
    if (message) setError(message);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="medium">Website</MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
            Create and manage the websites this tenant publishes from PearlCRM.
          </MDTypography>
        </MDBox>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
            action={loadFailed ? <MDButton size="small" color="error" onClick={state.reload}>Retry</MDButton> : undefined}
          >
            {error}
          </Alert>
        )}
        {loading && <MDBox display="flex" justifyContent="center" py={8}><CircularProgress color="info" /></MDBox>}
        {showWizard && (
          <WebsiteCreateWizard
            templates={templates}
            checkSlug={state.checkSlug}
            onCreate={onCreate}
            onCancel={sites.length ? () => setCreating(false) : null}
            onError={setError}
            saving={state.saving}
          />
        )}
        {!loading && !showWizard && site && (
          <>
            {justCreated && (isRealEstateSite(site) ? (
              <Alert
                severity="success"
                sx={{ mb: 3 }}
                onClose={() => setJustCreated(false)}
                action={<MDButton size="small" color="success" onClick={() => { setJustCreated(false); setSearchParams({ tab: "listings" }); }}>Choose listings</MDButton>}
              >
                {site.name} is ready. Choose which of your listings appear on it.
              </Alert>
            ) : (
              <Alert
                severity="success"
                sx={{ mb: 3 }}
                onClose={() => setJustCreated(false)}
                action={<MDButton size="small" color="success" onClick={onOpenBuilder}>Open builder</MDButton>}
              >
                {site.name} is ready. Open the storefront builder to make the home page your own.
              </Alert>
            ))}
            <WebsiteOverview
              state={state}
              onCreateAnother={() => { setJustCreated(false); setCreating(true); }}
              onOpenBuilder={onOpenBuilder}
            />
          </>
        )}
        <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice(null)} message={notice} />
      </MDBox>
    </DashboardLayout>
  );
}

export default WebsiteSettingsPage;
